import { useEffect, useMemo, useState } from "react";
import { NMAxiosInst } from "@neumetric/blueprint-lib/store";
import { DateTimeUtil } from "@neumetric/blueprint-lib/utility";
import { NMStatsTable, BarChart, DonutChart, Col, NMWaitIndicator } from "@neumetric/blueprint-lib/components";
import { ExportSection } from "../ExportSection";

const AssessmentWiseCoverageColumns = [
  {
    attribute: "assessments",
    title: "",
    width: "40%",
  },
  {
    attribute: "coverage",
    title: "Coverage",
    width: "12%",
    highlight: { header: true, cell: true },
    overallFlag: true,
  },
  { attribute: "total", title: "Applicable", width: "12%" },
  { attribute: "cleared", title: "Cleared", id: "COMPLETE", width: "12%" },
  { attribute: "pending", title: "Pending", id: "OPEN", width: "12%" },
  { attribute: "exempted", title: "Exempted", id: "INVALID", width: "12%" },
];

const DepartmentWiseCoverageColumns = [
  { attribute: "dept", title: "" },
  {
    attribute: "coverage",
    title: "Coverage",
    highlight: { header: true, cell: true },
    overallFlag: true,
  },
  { attribute: "total", title: "Applicable", width: "12%" },
  { attribute: "cleared", title: "Cleared", id: "COMPLETE" },
  { attribute: "pending", title: "Pending", id: "PENDING" },
  { attribute: "exempted", title: "Exempted", id: "INVALID" },
];

export function EducatorStats({ user, departments, mode, dateRange }) {
  const [assessmentRows, setAssessmentRows] = useState([]);
  const [departmentRows, setDepartmentRows] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const safeNum = (v) => (v === "-" || v == null ? 0 : Number(v));
  const effectiveRangeId =
    typeof dateRange === "string" ? dateRange : dateRange?.id || "last_365_days";

  // ---------- fetch ----------
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      const payload = { date_range_type: effectiveRangeId };

      try {
        const [assessRes, deptRes] = await Promise.all([
          NMAxiosInst.post("/educators/central_overall_coverage_dashboard/?page=1&per_page=100", payload),
          NMAxiosInst.post("/educators/central_department_converage_dashboard/?page=1&per_page=100", payload),
        ]);
        if (cancelled) return;

        // --- Assessments (overall_coverage) ---
        const assessArr = assessRes?.overall_coverage;
        if (!Array.isArray(assessArr) || assessArr.length === 0) {
          setAssessmentRows([]); // blank -> show "No Data To Display!"
        } else {
          const rows = assessArr.map(([name, stats]) => {
            const total = stats?.total || 0;
            const cleared = stats?.cleared || 0;
            const pending = stats?.pending || 0;
            const exempted = Math.max(0, total - (cleared + pending));
            return {
              assessments: name || "",
              coverage: stats?.coverage || 0,
              total,
              cleared,
              pending,
              exempted,
            };
          });

          const totalRow = rows.reduce(
            (acc, r) => ({
              ...acc,
              total: acc.total + (Number(r.total) || 0),
              cleared: acc.cleared + (Number(r.cleared) || 0),
              pending: acc.pending + (Number(r.pending) || 0),
              exempted: acc.exempted + (Number(r.exempted) || 0),
            }),
            {
              assessments: user.org.brandname,
              coverage: 0,
              total: 0,
              cleared: 0,
              pending: 0,
              exempted: 0,
              highlight: true,
            }
          );

          totalRow.coverage = totalRow.total > 0 ? `${Math.ceil((totalRow.cleared / totalRow.total) * 100)}` : "-";

          const pretty = [totalRow, ...rows].map((r) => {
            const out = { ...r };
            Object.keys(out).forEach((k) => {
              if (out[k] === 0 || out[k] === "0") out[k] = "-";
            });
            return out;
          });

          setAssessmentRows(pretty);
        }

        // --- Department coverage (department_coverage) ---
        const deptArr = deptRes?.department_coverage || [];
        if (!Array.isArray(deptArr) || deptArr.length === 0) {
          setDepartmentRows([]); // blank -> show "No Data To Display!"
          return; // ⬅️ stop here, don’t normalize empty response
        }

        let data = departments.map((dept) => {
          const deptRow = deptArr.find((r) => r.dept === dept.id) || {};
          return {
            dept: dept.id,
            coverage: deptRow.coverage || "0%",
            total: deptRow.total || 0,
            cleared: deptRow.cleared || 0,
            pending: deptRow.pending || 0,
            exempted: deptRow.exempted || 0,
          };
        });

        const totalRow = data.reduce(
          (acc, r) => ({
            ...acc,
            total: acc.total + (Number(r.total) || 0),
            cleared: acc.cleared + (Number(r.cleared) || 0),
            pending: acc.pending + (Number(r.pending) || 0),
            exempted: acc.exempted + (Number(r.exempted) || 0),
          }),
          {
            dept: user.org.brandname,
            coverage: 0,
            total: 0,
            cleared: 0,
            pending: 0,
            exempted: 0,
            highlight: true,
          }
        );

        totalRow.coverage = totalRow.total > 0 ? `${Math.ceil((totalRow.cleared / totalRow.total) * 100)}%` : "-";

        data.sort((a, b) =>
          String(a.dept || "")
            .toLowerCase()
            .localeCompare(String(b.dept || "").toLowerCase())
        );

        data = [totalRow, ...data].map((row) => {
          const out = { ...row };
          Object.keys(out).forEach((k) => {
            if (k !== "coverage" && (out[k] === 0 || out[k] === "0")) {
              out[k] = "-";
            }
          });
          return out;
        });

        setDepartmentRows(data);
      } catch (e) {
        if (!cancelled) {
          setAssessmentRows([]);
          setDepartmentRows([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [effectiveRangeId, departments, user.org.brandname]);

  // ---------- department bar chart ----------
  const departmentChartDatasets = useMemo(() => {
    const items = departments.map((dept) => {
      const deptRow = (departmentRows || []).find((r) => r.dept === dept.id);

      return {
        name: dept.id,
        cleared: deptRow ? (deptRow.cleared === "-" ? 0 : Number(deptRow.cleared)) : 0,
        pending: deptRow ? (deptRow.pending === "-" ? 0 : Number(deptRow.pending)) : 0,
      };
    });

    return Object.fromEntries(
      items.map((it) => [
        it.name,
        [
          { key: "Pending", label: "Pending", value: it.pending },
          { key: "Complete", label: "Cleared", value: it.cleared },
        ],
      ])
    );
  }, [departmentRows, departments]);

  // build donuts for each assessment row (skip brand row at [0])
  const assessmentDonuts = useMemo(() => {
    const list = (assessmentRows || []).slice(1); // skip brand/total
    return list.map((r, idx) => {
      const total = safeNum(r.total);
      const exempted = safeNum(r.exempted);
      const cleared = safeNum(r.cleared);

      const effectiveTotal = total - exempted; // remove exempted from total
      const coveragePct = effectiveTotal > 0 ? Math.round((cleared * 100) / effectiveTotal) : 0;

      return {
        key: `${r.assessments}-${idx}`,
        title: r.assessments,
        total,
        coveragePct,
        datasets: [
          {
            key: "CLEARED",
            value: cleared,
            label: "Cleared",
            sortOrder: 3,
            legendOrder: 1,
            textColor: "#FFF",
          },
          {
            key: "GAP",
            value: safeNum(r.pending),
            label: "Pending",
            sortOrder: 2,
            legendOrder: 2,
            textColor: "#FFF",
          },
          {
            key: "SKIPPED",
            value: exempted,
            label: "Exempted",
            border: true,
            sortOrder: 1,
            legendOrder: 3,
          },
        ],
      };
    });
  }, [assessmentRows]);

  const overallDonutDataset = useMemo(() => {
    const totalRow = assessmentRows[0];
    // Extract numeric part of coverage (remove % and convert to number)
    const coverageValue = totalRow?.coverage ? Number(String(totalRow.coverage).replace("%", "").trim()) || 0 : 0;
    return {
      total: totalRow?.total,
      totalCoveragePct: coverageValue,
      datasets: [
        {
          key: "CLEARED",
          value: safeNum(totalRow?.cleared),
          label: "Cleared",
          sortOrder: 3,
          legendOrder: 1,
          textColor: "#FFF",
        },
        {
          key: "GAP",
          value: safeNum(totalRow?.pending),
          label: "Pending",
          sortOrder: 2,
          legendOrder: 2,
          textColor: "#FFF",
        },
        {
          key: "SKIPPED",
          value: safeNum(totalRow?.exempted),
          label: "Exempted",
          border: true,
          sortOrder: 1,
          legendOrder: 3,
        },
      ],
    };
  }, [assessmentRows]);
  const donutDatasetToTable = (overallDonutDataset = {}) => {
    const { datasets = [] } = overallDonutDataset;

    const totals = {};
    datasets.forEach((d) => {
      const key = d.key?.toUpperCase();
      totals[key] = Number(d.value) || 0;
    });
    const DonutColumns = [
      { title: "Cleared", attribute: "CLEARED" },
      { title: "Pending", attribute: "PENDING" },
      { title: "Exempted", attribute: "EXEMPTED" },
      { title: "Coverage (%)", attribute: "COVERAGE" },
    ];
    const DonutRows = [
      {
        CLEARED: totals.CLEARED || 0,
        PENDING: totals.GAP || 0, // "GAP" key represents Pending
        EXEMPTED: totals.SKIPPED || 0,
        COVERAGE: `${overallDonutDataset.totalCoveragePct}%` || 0,
      },
    ];

    return { DonutColumns, DonutRows };
  };

  const { DonutColumns, DonutRows } = donutDatasetToTable(overallDonutDataset);

  if (isLoading) return <NMWaitIndicator />;

  return (
    <>
      {(mode === "full" || mode === "export_everything") && (
        <div className="o-auto">
          <div
            style={{
              display: "flex",
              width: "100%",
              padding: "30px 20px 20px 40px",
              gap: 20,
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            {/* Left: assessments table */}
            <div style={{ flex: "1 1 48%", minWidth: 420, maxWidth: "50%" }}>
              <ExportSection
                moduleKey={"Educator_Assessment_vs_Status"}
                index={1}
                exportType="table"
                exportData={{
                  title: "Education - Learning-wise",
                  columns: AssessmentWiseCoverageColumns,
                  rows: assessmentRows,
                }}
              >
                <NMStatsTable
                  key={`educator-assessments-${effectiveRangeId}`}
                  title="Education"
                  subTitle="Learning-wise"
                  columns={AssessmentWiseCoverageColumns}
                  dataSource={assessmentRows}
                  dateRange={dateRange?.label}
                  showDateInHeader={true}
                  initialRowLength={7}
                  getCellTypeRepresentation={(cellInfo) =>
                    cellInfo.rowId === -1 &&
                    !["coverage"].includes(cellInfo.columnId)
                      ? "DOT"
                      : null
                  }
                />
              </ExportSection>
            </div>

            {/* Right: one donut per assessment */}
            <Col className="w-719-px mt-12">
              <Col className="w-100 pl-8 nm-stats-cell" align="end" justify="end">
                <span>
                  <span className="ff-exo2 fs-10 fw-400 text-filled line-22 ml-2">
                    Status as on
                    <span className="fw-700 ml-2">{new DateTimeUtil().toDateFullYearString()}</span>.
                  </span>
                </span>
              </Col>
              <div
                style={{
                  width: 350,
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 20,
                }}
              >
                {assessmentDonuts.slice(0, 6).map((d, i) => (
                  <ExportSection
                    key={`${effectiveRangeId}-${d.key}`}
                    moduleKey="Educator_Assessment_vs_Status"
                    index={2 + i}
                    exportType="chart"
                    style={{
                      display: "inline-block",
                      width: 350,
                      flex: "0 0 auto",
                    }}
                  >
                    <div style={{ width: "100%", height: 220 }}>
                      <DonutChart
                        key={d.key}
                        chartId={`edu-assess-${effectiveRangeId}-${i}`}
                        title={d.title}
                        subtitle="Assessments:"
                        totalCount={String(d.total || "-")}
                        classes={{ root: `h-220-px` }}
                        datasets={d.datasets}
                        metric={{
                          label: "Coverage",
                          value: d.coveragePct,
                          type: "percent",
                        }}
                        style={{ width: "100%" }}
                      />
                    </div>
                  </ExportSection>
                ))}
              </div>
            </Col>
          </div>

          {/* Department-wise table + chart */}
          <div
            style={{
              display: "flex",
              width: "100%",
              padding: "20px 20px 20px 40px",
              gap: "20px",
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <ExportSection
              moduleKey={"Educator_Department_vs_Status"}
              index={1}
              exportType="table"
              exportData={{
                title: "Coverage - Department-wise",
                columns: DepartmentWiseCoverageColumns,
                rows: departmentRows,
              }}
              style={{
                flex: "1 1 48%",
                minWidth: 400,
                maxWidth: "50%",
              }}
            >
              <NMStatsTable
                key={`educator-departments-${effectiveRangeId}`}
                title="Coverage"
                subTitle="Department-wise"
                columns={DepartmentWiseCoverageColumns}
                dataSource={departmentRows}
                dateRange={dateRange?.label}
                showDateInHeader={true}
                getCellTypeRepresentation={(cellInfo) =>
                  cellInfo.rowId === -1 &&
                  cellInfo.columnId !== "coverage"
                    ? "DOT"
                    : null
                }
              />
            </ExportSection>

            <ExportSection
              moduleKey={"Educator_Department_vs_Status"}
              index={2}
              exportType="chart"
              style={{
                flex: "1 1 48%",
                minWidth: 400,
                maxWidth: "50%",
                paddingTop: 0,
                overflow: "visible",
              }}
            >
              <div className="chart-export-pad" style={{ width: "100%", overflow: "visible" }}>
                <div style={{ width: "100%", height: 560 }}>
                  <BarChart
                    title="Coverage"
                    subtitle="Department-wise"
                    datasets={departmentChartDatasets}
                    label={{ yAxis: "Department", xAxis: "Coverage" }}
                    stackType="100%"
                    showDateInHeader={true}
                    classes={{ root: "h-575-px w-100 mt-12" }}
                  />
                </div>
              </div>
            </ExportSection>
          </div>
          <div style={{ paddingTop: "100px" }}>
            <hr className="nm-hr nm-hr--center" />
          </div>
        </div>
      )}
      {(mode === "fusion" || mode === "export_everything") && (
        <div style={{ padding: "20px 0 0 10px" }}>
          <ExportSection
            moduleKey={"Educator_Overall_Coverage"}
            index={1}
            exportType={mode === "export_everything" ? "chart" : "table"}
            exportData={{
              title: "Education",
              columns: DonutColumns,
              rows: DonutRows,
            }}
            style={{
              display: "inline-block",
              width: 350,
              flex: "0 0 auto",
            }}
          >
            <Col style={{ flex: "1 1 0", width: 350 }}>
              <div style={{ width: "100%", height: 200 }}>
                <DonutChart
                  chartId="Educator_Overall_Coverage"
                  title="Education"
                  subtitle="Assessments:"
                  totalCount={String(overallDonutDataset.total || "-")}
                  classes={{ root: `h-200-px` }}
                  datasets={overallDonutDataset?.datasets || []}
                  metric={{
                    label: "Coverage",
                    value: overallDonutDataset.totalCoveragePct,
                    type: "percent",
                  }}
                  style={{ width: "100%" }}
                />
              </div>
            </Col>
          </ExportSection>
        </div>
      )}
    </>
  );
}
